export type BrowserLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface BrowserLogContext {
  [key: string]: unknown;
}

const SENSITIVE_KEY = /password|token|secret|authorization|cookie|mfa|totp|recovery|pan|passport|document|api[-_]?key/i;
const SENSITIVE_VALUE = /^(?:Bearer\s+\S+|eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)$/i;
const emittedOnce = new Set<string>();

function redact(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string' && SENSITIVE_VALUE.test(value)) return '[REDACTED]';
  if (Array.isArray(value)) return value.map((child) => redact(child, seen));
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return '[CIRCULAR]';
  seen.add(value);
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, child]) => [
      key,
      SENSITIVE_KEY.test(key) ? '[REDACTED]' : redact(child, seen),
    ]),
  );
}

function write(level: BrowserLogLevel, event: string, message: string, context: BrowserLogContext): void {
  const safeContext = redact(context) as BrowserLogContext;
  const entry = JSON.stringify({
    timestamp: new Date().toISOString(),
    level,
    event,
    message,
    ...safeContext,
  });
  if (level === 'error') console.error(entry);
  else if (level === 'warn') console.warn(entry);
  else console.log(entry);
}

export const browserLogger = {
  debug(event: string, message: string, context: BrowserLogContext = {}): void {
    if (import.meta.env.DEV || import.meta.env.MODE === 'test') write('debug', event, message, context);
  },
  info(event: string, message: string, context: BrowserLogContext = {}): void {
    write('info', event, message, context);
  },
  warn(event: string, message: string, context: BrowserLogContext = {}): void {
    write('warn', event, message, context);
  },
  error(event: string, message: string, context: BrowserLogContext = {}): void {
    write('error', event, message, context);
  },
  warnOnce(key: string, event: string, message: string, context: BrowserLogContext = {}): void {
    if (emittedOnce.has(key)) return;
    emittedOnce.add(key);
    write('warn', event, message, context);
  },
  resetForTests(): void {
    emittedOnce.clear();
  },
};
